import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { toast } from "sonner";
import FormField from "./FormField";
import FormSubmitButton from "./FormSubmitButton";
import { ThirdPartyLogin } from "./ThirdPartyLogin";
import { validateRegisterForm } from "@/utils/formValidation";
import { useAuth } from "@/context/AuthContext";
import { AlertCircle, Mail, Key, User, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

// 添加props接口
interface RegisterFormProps {
  onRegisterSuccess?: (email: string) => void;
  agreementAccepted?: boolean;
  onAgreementError?: () => void;
}

const RegisterForm = ({ onRegisterSuccess, agreementAccepted = false, onAgreementError }: RegisterFormProps = {}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    verificationCode: ""
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const navigate = useNavigate();
  const { toast: legacyToast } = useToast();
  const { register, loginWithProvider, checkEmailLegality, checkEmailExists, checkLastLogoff, sendOtp, verifyCode } = useAuth();

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  };

  // 处理倒计时逻辑
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && codeSent) {
      // 倒计时结束，允许重新发送
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, codeSent]);

  // 发送验证码
  const handleSendCode = async () => {
    // 如果倒计时还在进行中，不允许重复发送
    if (countdown > 0) return;

    if (!agreementAccepted) {
      setErrors({
        ...errors,
        email: "You must agree to our terms and conditions"
      });
      toast.error("Terms not accepted", {
        description: "You must agree to our terms and conditions to continue"
      });
      if (onAgreementError) {
        onAgreementError();
      }
      return;
    }

    if (!formData.email) {
      setErrors({
        ...errors,
        email: "Please enter your email address first"
      });
      return;
    }

    // 验证邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrors({
        ...errors,
        email: "Please enter a valid email address"
      });
      return;
    }

    setSendingCode(true);

    try {
      // 先验证邮箱是否合规
      const isEmailLegal = await checkEmailLegality(formData.email);

      if (isEmailLegal) {
        // 如果邮箱合规，检查是否已存在
        const emailExists = await checkEmailExists(formData.email);

        if (emailExists) {
          setErrors({
            ...errors,
            email: "This email is already registered. Please use a different email or login."
          });
          toast.error("Email Already Exists", {
            description: "This email is already registered. Please use a different email or login.",
          });
          return;
        }
      } else {
        // 如果邮箱不合规，检查最后一次注销信息
        const logoffResponse = await checkLastLogoff(formData.email);

        if (logoffResponse.code === 1000 && logoffResponse.data) {
          // 有注销信息，计算冷却期
          const logoffData = logoffResponse.data;

          if (logoffData.createTime) {
            // 计算7天后的时间戳
            const sevenDaysInMilliseconds = 7 * 24 * 60 * 60 * 1000;
            const timestampAfterSevenDays = logoffData.createTime + sevenDaysInMilliseconds;

            // 格式化日期为 MM/DD/YYYY 格式
            const logOffDate = new Date(timestampAfterSevenDays).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            });

            // 检查是否在冷却期内
            if (Date.now() < timestampAfterSevenDays) {
              const errorMsg = `You are unable to create a new account by this email until ${logOffDate} as you have deleted an account recently.`;

              setErrors({
                ...errors,
                email: errorMsg
              });
              toast.error("Registration Restricted", {
                description: errorMsg,
              });
              return;
            }
            // 如果已经过了冷却期，可以继续注册
          } else {
            // 如果没有创建时间但有注销记录，显示一般性错误
            const errorMsg = "This email cannot be used for registration due to a previous account deletion.";

            setErrors({
              ...errors,
              email: errorMsg
            });
            toast.error("Registration Restricted", {
              description: errorMsg,
            });
            return;
          }
        } else if (logoffResponse.code !== 1000) {
          // API调用出错
          const errorMsg = "Unable to verify account status. Please try again later.";

          setErrors({
            ...errors,
            email: errorMsg
          });
          toast.error("Verification Error", {
            description: errorMsg,
          });
          return;
        }

        // 没有注销信息但邮箱不合规，显示一般错误提示
        if (!logoffResponse.data) {
          setErrors({
            ...errors,
            email: "This email address is not valid or not allowed for registration."
          });
          toast.error("Invalid Email", {
            description: "This email address is not valid or not allowed for registration. Please use a different email.",
          });
          return;
        }
      }

      // 所有检查通过，发送验证码
      await sendOtp(formData.email, 'register');
      setCodeSent(true);
      // 设置60秒倒计时
      setCountdown(60);
      toast.success("Verification Code Sent", {
        description: "We've sent a verification code to your email. Please check your inbox.",
      });
    } catch (error: any) {
      toast.error("Error Sending Code", {
        description: error.message || "Failed to send verification code. Please try again.",
      });
    } finally {
      setSendingCode(false);
    }
  };

  // 验证表单数据
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!agreementAccepted) {
      newErrors.form = "You must agree to our terms and conditions";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.verificationCode.trim()) {
      newErrors.verificationCode = "Verification code is required";
    } else if (formData.verificationCode.length < 4) {
      newErrors.verificationCode = "Please enter a valid verification code";
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreementAccepted) {
      const newErrors = { form: "You must agree to our terms and conditions" };
      setErrors(newErrors);
      toast.error("Terms not accepted", {
        description: "You must agree to our terms and conditions to continue"
      });
      if (onAgreementError) {
        onAgreementError();
      }
      return;
    }

    const { isValid, errors: validationErrors } = validateRegisterForm(formData);

    if (!isValid) {
      setErrors(validationErrors);
      // 显示一个包含所有错误的通知
      const errorsList = Object.values(validationErrors).join(", ");
      toast.error("Registration failed", {
        description: errorsList
      });
      return;
    }

    setLoading(true);

    try {
      // 先验证验证码
      try {
        const isCodeValid = await verifyCode(formData.email, formData.verificationCode, 'register');
        if (!isCodeValid) {
          setErrors({
            ...errors,
            verificationCode: "Invalid verification code"
          });
          toast.error("Registration Error", {
            description: "Verification code is incorrect or has expired. Please try again."
          });
          setLoading(false);
          return;
        }
      } catch (codeError: any) {
        // 如果是验证码错误
        if (codeError.code === 40001 || (codeError.message && codeError.message.includes("40001"))) {
          setErrors({
            ...errors,
            verificationCode: "Invalid verification code"
          });
          toast.error("Registration Error", {
            description: "Verification code is incorrect or has expired. Please try again."
          });
          setLoading(false);
          return;
        }
        // 其他错误继续抛出
        throw codeError;
      }

      // 使用useAuth中的register方法
      await register(
        formData.email, // 使用email作为name参数
        formData.email,
        formData.password,
        formData.verificationCode
      );

      toast.success("Registration successful", {
        description: "Your account has been created successfully."
      });

      // 重定向到个人资料页面
      navigate('/profile');

      // 调用注册成功回调
      if (onRegisterSuccess) {
        onRegisterSuccess(formData.email);
      }

      // 保存邮箱到localStorage
      localStorage.setItem('anzir_lastEmail', formData.email);
    } catch (error: any) {
      console.error("Registration error:", error);

      // 提取错误信息
      let errorMessage = "Something went wrong. Please try again.";

      // 检查错误对象是否包含code字段
      if (error.code === 40001 || (error.response && error.response.code === 40001)) {
        errorMessage = "Verification code is incorrect or has expired. Please try again.";
        setErrors({
          ...errors,
          verificationCode: "Invalid verification code"
        });
      } else if (error.message) {
        // 检查错误消息中是否包含40001状态码的信息
        if (error.message.includes("40001")) {
          errorMessage = "Verification code is incorrect or has expired. Please try again.";
          setErrors({
            ...errors,
            verificationCode: "Invalid verification code"
          });
        }
        // 针对不同的错误提供具体信息
        else if (error.message.includes("check failed")) {
          errorMessage = "Your registration information could not be verified. Please check your details and try again.";
        } else if (error.message.includes("token")) {
          errorMessage = "Registration was successful, but login failed. Please try logging in.";
        } else {
          errorMessage = error.message;
        }
      }

      // 显示错误消息
      toast.error("Registration Error", {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle third-party registration/login
  const handleProviderLogin = async (provider: "google" | "apple" | "facebook") => {
    try {
      setLoading(true);
      await loginWithProvider(provider);

      legacyToast({
        title: "Registration Successful",
        description: `Your account has been created with ${provider}.`,
        variant: "default",
      });

      navigate('/profile');
    } catch (error) {
      legacyToast({
        title: "Authentication Error",
        description: `Failed to register with ${provider}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          id="email"
          name="email"
          label="Email Address"
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
        />

        <div>
          <label htmlFor="verificationCode" className="text-white block mb-2">
            Verification Code
          </label>
          <div className="flex space-x-2 items-center">
            <div className="flex-grow relative">
              <input
                id="verificationCode"
                name="verificationCode"
                type="text"
                placeholder="Enter verification code"
                className={`w-full h-10 rounded-md border bg-black/50 px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#ff6c24] focus-visible:border-[#ff6c24] ${
                  errors.verificationCode ? 'border-red-500' : 'border-white/20'
                }`}
                value={formData.verificationCode}
                onChange={handleChange}
              />
            </div>
            <Button
              type="button"
              onClick={handleSendCode}
              disabled={sendingCode || countdown > 0}
              className={countdown > 0
                ? "h-10 min-w-[120px] bg-gray-600/50 text-gray-400 cursor-not-allowed rounded-md text-sm font-medium"
                : "h-10 min-w-[120px] bg-[#ff6c24] hover:bg-[#ff6c24]/90 text-white rounded-md text-sm font-medium"}
            >
              {sendingCode ? (
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block animate-spin mr-2"></span>
              ) : countdown > 0 ? (
                <>
                  <span className="mr-1">Resend in</span>
                  <span className="font-bold">{countdown}s</span>
                </>
              ) : (
                codeSent ? "Resend Code" : "Send Code"
              )}
            </Button>
          </div>
          {errors.verificationCode && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <AlertCircle className="h-3 w-3 mr-1" />
              {errors.verificationCode}
            </p>
          )}
        </div>

        <FormField
          id="password"
          name="password"
          label="Password"
          type="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          showPasswordToggle={true}
          showPassword={showPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
        />

        <FormField
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm Password"
          type="password"
          placeholder="Confirm your password"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
          showPasswordToggle={false}
        />

        {errors.general && (
          <div className="bg-red-500/10 text-red-500 px-4 py-2 rounded flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{errors.general}</span>
          </div>
        )}

        <FormSubmitButton loading={loading}>
          Create Account
        </FormSubmitButton>
      </form>

      <ThirdPartyLogin variant="default" forRegister={true} />
    </div>
  );
};

export default RegisterForm;
